import Dexie, { Table } from 'dexie';
import { getEffectiveTenantHost } from '../config';
import type { FunctionAppVersion, TenantInfo } from './types';

/**
 * Origin-scoped Dexie database. Holds tenant configuration that is read
 * before a user id exists (tenant record, deployed API version).
 * It does not hold hazards, comments, reactions, sync cursors, or the
 * lexicon (tenant vocabulary lives in the per-user store).
 * The database name includes the host so two tenants on one browser do not share it.
 */

export interface DeviceMetaRow {
  key: string;
  value: string;
}

export class DeviceStore extends Dexie {
  tenantInfo!: Table<TenantInfo, string>;
  functionAppVersion!: Table<FunctionAppVersion, string>;
  meta!: Table<DeviceMetaRow, string>;

  constructor(name: string) {
    super(name);
    // Version 2 removes hazardLexicon: the lexicon moved to the per-user store.
    // The upgrade drops the orphaned table and its content on databases that
    // were opened at version 1; there is no data to migrate.
    this.version(2).stores({
      hazardLexicon: null,
    });
    this.version(1).stores({
      tenantInfo: 'id, subdomain, last_updated',
      functionAppVersion: '&key',
      hazardLexicon: 'id, term, category',
      meta: '&key',
    });
  }
}

const VERSION_KEY = 'functionAppVersion';

let current: DeviceStore | null = null;

export function deviceHost(): string {
  return getEffectiveTenantHost() || 'localhost';
}

export function deviceStoreName(host: string): string {
  return `safework-device:${encodeURIComponent(host)}`;
}

export async function openDeviceStore(host: string = deviceHost()): Promise<DeviceStore> {
  const name = deviceStoreName(host);
  if (current && current.name === name && current.isOpen()) {
    return current;
  }
  if (current) {
    current.close();
    current = null;
  }
  const db = new DeviceStore(name);
  await db.open();
  current = db;
  return db;
}

export async function closeDeviceStore(): Promise<void> {
  if (current) {
    current.close();
    current = null;
  }
}

export async function putTenantInfo(row: TenantInfo, host?: string): Promise<void> {
  const db = await openDeviceStore(host);
  await db.tenantInfo.put(row);
}

export async function listTenantInfo(host?: string): Promise<TenantInfo[]> {
  const db = await openDeviceStore(host);
  return db.tenantInfo.toArray();
}

export async function getFunctionAppVersion(host?: string): Promise<FunctionAppVersion | undefined> {
  const db = await openDeviceStore(host);
  return db.functionAppVersion.get(VERSION_KEY);
}

export async function putFunctionAppVersion(row: FunctionAppVersion, host?: string): Promise<void> {
  const db = await openDeviceStore(host);
  await db.functionAppVersion.put(row);
}

export async function touchFunctionAppVersion(lastChecked: string, host?: string): Promise<void> {
  const db = await openDeviceStore(host);
  await db.functionAppVersion.update(VERSION_KEY, { lastChecked });
}

export async function readDeviceMeta(key: string, host?: string): Promise<string | undefined> {
  const db = await openDeviceStore(host);
  const row = await db.meta.get(key);
  return row?.value;
}

export async function writeDeviceMeta(key: string, value: string, host?: string): Promise<void> {
  const db = await openDeviceStore(host);
  await db.meta.put({ key, value });
}

export async function deleteDeviceMeta(key: string, host?: string): Promise<void> {
  const db = await openDeviceStore(host);
  await db.meta.delete(key);
}
